'use client'

import { createContext, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'

export interface ViewportTrackingData<T = Record<string, unknown>> {
  id: string
  data?: T
}

interface ViewportTrackingContextType {
  registerElement: (id: string, data?: Record<string, unknown>) => void
}

export const ViewportTrackingContext = createContext<
  ViewportTrackingContextType | undefined
>(undefined)

interface ViewportTrackingProviderProps<T> {
  /**
   * Children to be tracked.
   */
  children: ReactNode

  /**
   * Callback function to track the data.
   */
  onTrack: (trackingData: Array<ViewportTrackingData<T>>) => void

  /**
   * Debounce time in milliseconds. If not set, the default is 300ms.
   */
  debounceMs?: number
}

export const ViewportTracking = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  children,
  onTrack,
  debounceMs = 300,
}: ViewportTrackingProviderProps<T>) => {
  const pendingElements = useRef<Map<string, ViewportTrackingData<T>>>(
    new Map(),
  )
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const flushPendingElements = useCallback(() => {
    if (pendingElements.current.size > 0) {
      const elements = Array.from(pendingElements.current.values())
      onTrack(elements)

      pendingElements.current.clear()
    }
  }, [onTrack])

  const scheduleFlush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      flushPendingElements()
      timeoutRef.current = null
    }, debounceMs)
  }, [debounceMs, flushPendingElements])

  const registerElement = useCallback(
    (id: string, data?: Record<string, unknown>) => {
      pendingElements.current.set(id, {
        id,
        data: data as ViewportTrackingData<T>['data'],
      })
      scheduleFlush()
    },
    [scheduleFlush],
  )

  return (
    <ViewportTrackingContext.Provider value={{ registerElement }}>
      {children}
    </ViewportTrackingContext.Provider>
  )
}
